import { useCallback, useEffect, useState } from "react";
import {
  useGetKnowledgeBaseAttachmentWithContentQuery,
  useUpdateKnowledgeBaseAttachmentContentMutation,
} from "../../../api/entities/knowledgeBase.js";

interface UseAttachmentEditorStateProps {
  knowledgeBaseId: string;
  attachmentId: string;
}

export function useAttachmentEditorState({
  knowledgeBaseId,
  attachmentId,
}: UseAttachmentEditorStateProps) {
  const { currentData: attachment, isLoading } =
    useGetKnowledgeBaseAttachmentWithContentQuery({
      knowledgeBaseId,
      attachmentId,
    });
  const [updateContent, { isLoading: isSaving }] =
    useUpdateKnowledgeBaseAttachmentContentMutation();

  const [editedBody, setEditedBody] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (attachment && !isInitialized) {
      setEditedBody(attachment.body ?? "");
      setIsInitialized(true);
    }
  }, [attachment, isInitialized]);

  const fetchedBody = attachment?.body ?? "";
  const hasUnsavedChanges = isInitialized && editedBody !== fetchedBody;

  const onSave = useCallback(
    async (body: string) => {
      await updateContent({ knowledgeBaseId, attachmentId, body }).unwrap();
    },
    [updateContent, knowledgeBaseId, attachmentId],
  );

  const onDiscard = useCallback(() => {
    setEditedBody(fetchedBody);
  }, [fetchedBody]);

  return {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
  };
}
